import React from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ItemLoadingHolder from '../../components/LoadingHolders/ItemLoadingHolder';

const AIReportModal = ({ report, loading, onClose }) => {
  // Safe helpers for deeply nested fields
  const safe = (fn, fallback = '–') => {
    try {
      const val = fn();
      if (val === undefined || val === null) return fallback;
      if (Array.isArray(val) && val.length === 0) return fallback;
      return val;
    } catch {
      return fallback;
    }
  };

  // Asigură-te că textul este păstrat cu diacritice fără a folosi escape/decodeURIComponent
  const removeDiacritics = (txt) =>
    typeof txt === 'string'
      ? txt
          .replace(/[ăâ]/gi, 'a')
          .replace(/[ĂÂ]/g, 'A')
          .replace(/[șş]/gi, 's')
          .replace(/[ȘŞ]/g, 'S')
          .replace(/[țţ]/gi, 't')
          .replace(/[ȚŢ]/g, 'T')
          .replace(/[î]/gi, 'i')
          .replace(/[Î]/g, 'I')
          .replace(/[éèêë]/gi, 'e')
          .replace(/[ÉÈÊË]/g, 'E')
      : txt;
  const safeText = removeDiacritics;
  // Explicații pentru fiecare câmp
  const fieldDescriptions = {
    project_title: safeText('Titlul complet al achizitiei sau proiectului.'),
    cpv_codes: safeText(
      'Coduri CPV (Common Procurement Vocabulary) care clasifica tipul lucrarilor.',
    ),
    uniclass_codes: safeText(
      'Coduri UNICLASS – folosite international pentru clasificarea elementelor de constructii.',
    ),
    caen_codes: safeText(
      'Coduri CAEN – clasificarea activitatilor economice din Romania.',
    ),
    cor_experts: safeText(
      'Tipurile de experti COR implicati (ingineri, manageri de proiect etc.).',
    ),
    primary_domains: safeText(
      'Domenii principale relevante (ex: constructii, instalatii, mediu).',
    ),
    project_keywords: safeText(
      'Cuvinte cheie pentru cautari si clasificare (ex: “hala metalica”, “prefabricate”).',
    ),
    document_type: safeText(
      'Tipul documentului analizat (ex: “caiet de sarcini”).',
    ),
    estimated_duration_months: safeText('Durata estimata in luni (ex: 18).'),
    estimated_value: safeText('Valoarea estimata (in lei sau euro).'),
    has_environmental_impact: safeText(
      'Indica daca proiectul are impact de mediu.',
    ),
    includes_green_space: safeText('Include amenajari de spatii verzi.'),
    includes_demolition: safeText('Include demolari existente.'),
    includes_infrastructure: safeText(
      'Include lucrari de infrastructura (ex: utilitati, drumuri).',
    ),
    ai_tags: safeText(
      'Etichete generate automat, utile pentru AI si filtrare.',
    ),
    technology_level: safeText(
      'Gradul de tehnologizare (manual, automatizat, smart building etc.).',
    ),
    building_type: safeText('Tipul constructiei (hala, cladire birouri etc.).'),
    beneficiary_type: safeText(
      'Cine este beneficiarul proiectului (primarie, ANL, CJ etc.).',
    ),
    is_rehabilitation: safeText('Este reabilitare sau constructie noua.'),
    is_part_of_larger_investment_project: safeText(
      'Specifica daca face parte dintr-un proiect mai mare.',
    ),
  };
  // Helper pentru listare frumoasă
  const formatList = (arr) =>
    Array.isArray(arr)
      ? arr.map((item) => '- ' + safeText(item)).join('\n')
      : safeText(arr);
  const downloadAsPDF = () => {
    const doc = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: true,
    });
    // Titlu mare Go4Projects
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(safeText('Go4Projects – Rapoarte AI'), 105, 18, {
      align: 'center',
    });
    // Subtitlu cu nume procedură dacă există
    doc.setFontSize(13);
    if (report.project_title) {
      doc.text(safeText(report.project_title), 105, 26, { align: 'center' });
    }
    if (report.procedure_type || report.procedure_number) {
      let proc = '';
      if (report.procedure_type) proc += safeText(report.procedure_type);
      if (report.procedure_number)
        proc += ' – ' + safeText(report.procedure_number);
      doc.setFontSize(11);
      doc.text(proc, 105, 33, { align: 'center' });
    }
    // Tabelul începe mai jos
    autoTable(doc, {
      startY: 38,
      head: [[safeText('Câmp'), safeText('Explicație'), safeText('Valoare')]],
      body: [
        [
          safeText('Titlu proiect'),
          fieldDescriptions.project_title,
          safeText(report.project_title),
        ],
        [
          safeText('Coduri CPV'),
          fieldDescriptions.cpv_codes,
          formatList(
            (report.cpv_codes || []).map((c) => `${c.code} - ${c.description}`),
          ),
        ],
        [
          safeText('Coduri UNICLASS'),
          fieldDescriptions.uniclass_codes,
          formatList(
            (report.uniclass_codes || []).map((c) => `${c.code} - ${c.title}`),
          ),
        ],
        [
          safeText('Coduri CAEN'),
          fieldDescriptions.caen_codes,
          formatList(
            (report.caen_codes || []).map(
              (c) => `${c.code} - ${c.description}`,
            ),
          ),
        ],
        [
          safeText('Experți COR'),
          fieldDescriptions.cor_experts,
          formatList(
            (report.cor_experts || []).map(
              (c) => `${c.role} (${c.cor_code}) - ${c.description}`,
            ),
          ),
        ],
        [
          safeText('Domenii principale'),
          fieldDescriptions.primary_domains,
          formatList(report.metadata?.primary_domains || []),
        ],
        [
          safeText('Cuvinte cheie'),
          fieldDescriptions.project_keywords,
          formatList(report.metadata?.project_keywords || []),
        ],
        [
          safeText('Tip document'),
          fieldDescriptions.document_type,
          safeText(report.metadata?.document_type),
        ],
        [
          safeText('Durată estimată'),
          fieldDescriptions.estimated_duration_months,
          safeText(`${report.metadata?.estimated_duration_months || ''} luni`),
        ],
        [
          safeText('Valoare estimată'),
          fieldDescriptions.estimated_value,
          safeText(
            `${(report.metadata?.estimated_value || '').toLocaleString('ro-RO')} RON`,
          ),
        ],
        [
          safeText('Impact mediu'),
          fieldDescriptions.has_environmental_impact,
          safeText(report.metadata?.has_environmental_impact ? 'Da' : 'Nu'),
        ],
        [
          safeText('Include spațiu verde'),
          fieldDescriptions.includes_green_space,
          safeText(report.metadata?.includes_green_space ? 'Da' : 'Nu'),
        ],
        [
          safeText('Include demolări'),
          fieldDescriptions.includes_demolition,
          safeText(report.metadata?.includes_demolition ? 'Da' : 'Nu'),
        ],
        [
          safeText('Include infrastructură'),
          fieldDescriptions.includes_infrastructure,
          safeText(report.metadata?.includes_infrastructure ? 'Da' : 'Nu'),
        ],
        [
          safeText('Taguri AI'),
          fieldDescriptions.ai_tags,
          formatList(report.metadata?.ai_tags || []),
        ],
        [
          safeText('Tehnologie'),
          fieldDescriptions.technology_level,
          safeText(report.metadata?.technology_level),
        ],
        [
          safeText('Tip construcție'),
          fieldDescriptions.building_type,
          safeText(report.metadata?.building_type),
        ],
        [
          safeText('Beneficiar'),
          fieldDescriptions.beneficiary_type,
          safeText(report.metadata?.beneficiary_type),
        ],
        [
          safeText('Este reabilitare'),
          fieldDescriptions.is_rehabilitation,
          safeText(report.metadata?.is_rehabilitation ? 'Da' : 'Nu'),
        ],
        [
          safeText('Parte din proiect mai mare'),
          fieldDescriptions.is_part_of_larger_investment_project,
          safeText(
            report.is_part_of_larger_investment_project
              ?.is_part_of_larger_investment_project
              ? 'Da'
              : 'Nu',
          ),
        ],
        [
          safeText('Nume proiect mare'),
          'Numele proiectului mai mare.',
          safeText(
            report.is_part_of_larger_investment_project?.project_name || '–',
          ),
        ],
        [
          safeText('Dovadă proiect mare'),
          'Dovadă proiect investiție majoră.',
          safeText(
            report.is_part_of_larger_investment_project?.evidence || '–',
          ),
        ],
        report.procedure_type
          ? [
              safeText('Tip procedură'),
              'Tipul procedurii asociate proiectului.',
              safeText(report.procedure_type),
            ]
          : null,
        report.procedure_number
          ? [
              safeText('Număr procedură'),
              'Numărul procedurii asociate proiectului.',
              safeText(report.procedure_number),
            ]
          : null,
        report.procedure_status
          ? [
              safeText('Status procedură'),
              'Statusul actual al procedurii.',
              safeText(report.procedure_status),
            ]
          : null,
      ].filter(Boolean),
      styles: { font: 'helvetica', fontStyle: 'normal', fontSize: 10 },
      headStyles: { fillColor: [99, 102, 241], textColor: 255 },
      bodyStyles: { textColor: 20 },
      columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 70 },
        2: { cellWidth: 65 },
      },
      margin: { left: 10, right: 10 },
      encoding: 'UTF-8',
      didParseCell: function (data) {
        // Pentru a păstra newlines la export, transformă în multi-line
        if (typeof data.cell.raw === 'string' && data.cell.raw.includes('\n')) {
          data.cell.styles.cellPadding = 2;
        }
      },
    });
    doc.save('Raport_AI.pdf');
  };

  if (!report || typeof report !== 'object') {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fadeIn">
        <div className="relative bg-gradient-to-br from-white via-indigo-50 to-blue-50 p-10 rounded-3xl shadow-2xl w-11/12 max-w-4xl mx-auto overflow-y-auto max-h-[95vh] border border-indigo-100 animate-slideUp">
          <button
            className="absolute top-4 right-4 text-red-500 bg-white rounded-full shadow-lg p-3 hover:bg-red-50 hover:scale-110 transition z-10"
            onClick={onClose}
            aria-label="Închide"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <h2 className="text-2xl font-bold text-red-600 text-center mb-4">
            Eroare la încărcarea raportului AI
          </h2>
          <p className="text-center text-gray-700">
            Raportul nu a putut fi încărcat sau este corupt. Încearcă din nou
            sau contactează suportul.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div className="relative bg-gradient-to-br from-white via-indigo-50 to-blue-50 p-6 md:p-10 rounded-3xl shadow-2xl w-11/12 max-w-4xl mx-auto overflow-y-auto max-h-[95vh] border border-indigo-100 animate-slideUp">
        {/* Buton PDF sus-dreapta */}
        <button
          onClick={downloadAsPDF}
          className="absolute top-4 right-20 md:right-24 bg-gradient-to-r from-indigo-600 to-blue-500 text-white font-semibold py-2 px-4 rounded-xl hover:from-indigo-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 shadow-lg transition text-base flex items-center gap-2 z-20"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Descarcă PDF
        </button>
        {/* Close button */}
        <button
          className="absolute top-4 right-4 text-red-500 bg-white rounded-full shadow-lg p-3 hover:bg-red-50 hover:scale-110 transition z-10"
          onClick={onClose}
          aria-label="Închide"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-7 w-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <h2 className="text-3xl font-extrabold mb-6 text-indigo-800 text-center drop-shadow-sm tracking-tight">
          Raport AI – Sinteză proiect
        </h2>
        {loading ? (
          <div className="text-center">
            <ItemLoadingHolder />
            <p className="mt-2 text-base text-gray-500">
              Generare raport în curs...
            </p>
          </div>
        ) : (
          <div className="space-y-8 text-base max-h-[70vh] overflow-auto">
            {/* Secțiune info cheie */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-white/80 rounded-2xl p-6 shadow mb-2 border border-indigo-100">
              <div className="space-y-3">
                <div>
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Titlu proiect
                  </span>
                  <span className="font-bold text-indigo-800 text-xl md:text-2xl break-words">
                    {safe(() => report.project_title)}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="inline-block bg-indigo-100 text-indigo-700 text-xs font-semibold px-3 py-1 rounded-full">
                    {safe(() => report.metadata.document_type)}
                  </span>
                  <span className="inline-block bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">
                    {safe(() =>
                      report.metadata.estimated_value.toLocaleString('ro-RO'),
                    )}{' '}
                    RON
                  </span>
                  <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                    {safe(() => report.metadata.estimated_duration_months)} luni
                  </span>
                </div>
                <div className="mt-2">
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Domenii principale
                  </span>
                  <span className="font-semibold text-indigo-700">
                    {safe(() => report.metadata.primary_domains.join(', '))}
                  </span>
                </div>
                <div className="mt-2">
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Taguri AI
                  </span>
                  <span className="inline-block bg-yellow-100 text-yellow-800 text-xs font-semibold px-3 py-1 rounded-full">
                    {safe(() => report.metadata.ai_tags.join(', '))}
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Cuvinte cheie
                  </span>
                  <span className="text-gray-800">
                    {safe(() => report.metadata.project_keywords.join(', '))}
                  </span>
                </div>
                <div className="mt-2">
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Tip construcție
                  </span>
                  <span className="text-gray-800">
                    {safe(() => report.metadata.building_type)}
                  </span>
                </div>
                <div className="mt-2">
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Beneficiar
                  </span>
                  <span className="text-gray-800">
                    {safe(() => report.metadata.beneficiary_type)}
                  </span>
                </div>
                <div className="mt-2">
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Tehnologie
                  </span>
                  <span className="text-gray-800">
                    {safe(() => report.metadata.technology_level)}
                  </span>
                </div>
              </div>
            </div>
            {/* Restul info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div className="space-y-3">
                <div>
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Impact mediu
                  </span>
                  <span
                    className={
                      safe(() => report.metadata.has_environmental_impact) ===
                      'Da'
                        ? 'inline-block bg-green-100 text-green-700 font-semibold px-3 py-1 rounded-full'
                        : 'inline-block bg-gray-100 text-gray-500 font-semibold px-3 py-1 rounded-full'
                    }
                  >
                    {safe(() =>
                      report.metadata.has_environmental_impact ? 'Da' : 'Nu',
                    )}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Include spațiu verde
                  </span>
                  <span
                    className={
                      safe(() => report.metadata.includes_green_space) === 'Da'
                        ? 'inline-block bg-green-100 text-green-700 font-semibold px-3 py-1 rounded-full'
                        : 'inline-block bg-gray-100 text-gray-500 font-semibold px-3 py-1 rounded-full'
                    }
                  >
                    {safe(() =>
                      report.metadata.includes_green_space ? 'Da' : 'Nu',
                    )}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Include demolare
                  </span>
                  <span
                    className={
                      safe(() => report.metadata.includes_demolition) === 'Da'
                        ? 'inline-block bg-green-100 text-green-700 font-semibold px-3 py-1 rounded-full'
                        : 'inline-block bg-gray-100 text-gray-500 font-semibold px-3 py-1 rounded-full'
                    }
                  >
                    {safe(() =>
                      report.metadata.includes_demolition ? 'Da' : 'Nu',
                    )}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Include infrastructură
                  </span>
                  <span
                    className={
                      safe(() => report.metadata.includes_infrastructure) ===
                      'Da'
                        ? 'inline-block bg-green-100 text-green-700 font-semibold px-3 py-1 rounded-full'
                        : 'inline-block bg-gray-100 text-gray-500 font-semibold px-3 py-1 rounded-full'
                    }
                  >
                    {safe(() =>
                      report.metadata.includes_infrastructure ? 'Da' : 'Nu',
                    )}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Este reabilitare
                  </span>
                  <span
                    className={
                      safe(() => report.metadata.is_rehabilitation) === 'Da'
                        ? 'inline-block bg-green-100 text-green-700 font-semibold px-3 py-1 rounded-full'
                        : 'inline-block bg-gray-100 text-gray-500 font-semibold px-3 py-1 rounded-full'
                    }
                  >
                    {safe(() =>
                      report.metadata.is_rehabilitation ? 'Da' : 'Nu',
                    )}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Parte din proiect major
                  </span>
                  <span
                    className={
                      safe(
                        () =>
                          report.is_part_of_larger_investment_project
                            .is_part_of_larger_investment_project,
                      ) === 'Da'
                        ? 'inline-block bg-green-100 text-green-700 font-semibold px-3 py-1 rounded-full'
                        : 'inline-block bg-gray-100 text-gray-500 font-semibold px-3 py-1 rounded-full'
                    }
                  >
                    {safe(() =>
                      report.is_part_of_larger_investment_project
                        .is_part_of_larger_investment_project
                        ? 'Da'
                        : 'Nu',
                    )}
                  </span>
                </div>
                {safe(
                  () =>
                    report.is_part_of_larger_investment_project.project_name,
                  null,
                ) && (
                  <div>
                    <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                      Nume proiect mare
                    </span>
                    <span className="text-gray-800">
                      {safe(
                        () =>
                          report.is_part_of_larger_investment_project
                            .project_name,
                      )}
                    </span>
                  </div>
                )}
                {safe(
                  () => report.is_part_of_larger_investment_project.evidence,
                  null,
                ) && (
                  <div>
                    <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                      Dovadă proiect major
                    </span>
                    <span className="text-gray-800">
                      {safe(
                        () =>
                          report.is_part_of_larger_investment_project.evidence,
                      )}
                    </span>
                  </div>
                )}
                {safe(() => report.procedure_type, null) && (
                  <div>
                    <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                      Tip procedură
                    </span>
                    <span className="text-gray-800">
                      {safe(() => report.procedure_type)}
                    </span>
                  </div>
                )}
                {safe(() => report.procedure_number, null) && (
                  <div>
                    <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                      Număr procedură
                    </span>
                    <span className="text-gray-800">
                      {safe(() => report.procedure_number)}
                    </span>
                  </div>
                )}
                {safe(() => report.procedure_status, null) && (
                  <div>
                    <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                      Status procedură
                    </span>
                    <span className="text-gray-800">
                      {safe(() => report.procedure_status)}
                    </span>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Coduri CPV
                  </span>
                  <ul className="space-y-1 ml-2">
                    {(report.cpv_codes || []).map((c, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-indigo-400 rounded-full inline-block"></span>
                        <span className="text-gray-800">
                          {c.code} - {c.description}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Coduri CAEN
                  </span>
                  <ul className="space-y-1 ml-2">
                    {(report.caen_codes || []).map((c, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-400 rounded-full inline-block"></span>
                        <span className="text-gray-800">
                          {c.code} - {c.description}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Coduri UNICLASS
                  </span>
                  <ul className="space-y-1 ml-2">
                    {(report.uniclass_codes || []).map((c, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-400 rounded-full inline-block"></span>
                        <span className="text-gray-800">
                          {c.code} - {c.title}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Experți COR
                  </span>
                  <ul className="space-y-1 ml-2">
                    {(report.cor_experts || []).map((c, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-yellow-400 rounded-full inline-block"></span>
                        <span className="text-gray-800">
                          {c.role} ({c.cor_code}) - {c.description}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIReportModal;
